import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useBusiness } from "@/core/business";
import { getPayBreakdownPath } from "@/payroll/pay-breakdown/routes";
import { useGetPayPeriods } from "@/payroll/pay-periods/query";
import { useGetMyUser } from "@/user/query";
import type { PeriodOption } from "./types";
import { sortPayPeriodsDesc, toPeriodOption } from "./utils";

const hasCompletedPayrollOutput = (period: {
  latest_completed_ai_script_run_id: string | null;
  latest_completed_icm_run_id: string | null;
}) =>
  period.latest_completed_ai_script_run_id != null ||
  period.latest_completed_icm_run_id != null;

export const useTechnicianPayrollBase = ({
  redirectToDefaultPeriod = true,
}: {
  redirectToDefaultPeriod?: boolean;
} = {}) => {
  const navigate = useNavigate();
  const { periodStart } = useParams<{ periodStart?: string }>();
  const { businessId } = useBusiness();
  const { data: techUser, isLoading: isUserLoading } = useGetMyUser();
  const {
    data: payPeriods = [],
    isLoading: isPayPeriodsLoading,
    isError: isPayPeriodsError,
  } = useGetPayPeriods(businessId);

  const sortedPayPeriods = useMemo(
    () => sortPayPeriodsDesc(payPeriods),
    [payPeriods],
  );

  const periodOptions = useMemo<PeriodOption[]>(
    () =>
      sortedPayPeriods.filter(hasCompletedPayrollOutput).map(toPeriodOption),
    [sortedPayPeriods],
  );

  useEffect(() => {
    if (!redirectToDefaultPeriod || periodStart) return;

    const defaultPeriod = sortedPayPeriods.find(hasCompletedPayrollOutput);
    if (!defaultPeriod) return;

    navigate(getPayBreakdownPath(defaultPeriod.start_date), {
      replace: true,
    });
  }, [navigate, periodStart, redirectToDefaultPeriod, sortedPayPeriods]);

  return {
    businessId,
    techUser,
    isUserLoading,
    sortedPayPeriods,
    periodOptions,
    periodStart,
    onPeriodChange: (value: string) => navigate(getPayBreakdownPath(value)),
    isPayPeriodsLoading,
    isPayPeriodsError,
  };
};
